"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { RefreshCw, X } from "lucide-react"
import { useI18n } from "@/i18n"
import { LeftRail } from "./LeftRail"
import { BottomNav } from "./BottomNav"
import { AmyDrawer } from "./AmyDrawer"
import { onOpenAmyWithPrompt } from "@/lib/amyLauncher"
import { CLIENT_BUILD_ID } from "@/lib/buildInfo"
import { useDeployFreshness, type DeployFreshnessOptions } from "@/lib/deployFreshness"
import { stashPendingDraft, stashPendingPrefill, takePendingPrefill } from "@/lib/amyPrefillHandoff"
import { peekAmyDraft } from "@/lib/amyDraft"
import { isPrivacyDataPath, useHealthConsentRequired } from "@/lib/healthConsentGate"
import { HealthConsentGate } from "@/components/consent/HealthConsentGate"
import { ConsentExemptFrame } from "@/components/consent/ConsentExemptFrame"

export interface AppShellProps {
  /** Test seam for the freshness owner (fetch/clock/build id); production uses the defaults. */
  freshness?: DeployFreshnessOptions
  /** Test seam: the reload the shell performs (defaults to window.location.reload). */
  reload?: () => void
}

function defaultReload(): void {
  window.location.reload()
}

/**
 * Authenticated /app shell (T09): desktop left rail + mobile bottom nav,
 * dismissible soft-launch banner, and the Amy drawer/sheet toggle. Feature
 * screens render into <Outlet /> and own their own loading/empty/error
 * states.
 *
 * Acceptance 4 (2026-09-04) — deploy freshness. A single-page app never
 * reloads its JavaScript by itself, so a long-lived tab kept executing a
 * superseded bundle across a deploy (proven: it sent the OLD Amy prefill).
 * The shell now (a) shows a calm, dismissable "new version" banner when the
 * deployed build id differs from the executing one, and (b) before handing
 * a CTA prefill to Amy, re-checks and — when stale — stashes the prompt (and
 * any unsent composer text), reloads, and reopens the drawer on the NEW
 * bundle. The reload happens at most once per handoff: a stash whose build
 * id is not the one that booted is dropped, never retried.
 */
export function AppShell({ freshness, reload = defaultReload }: AppShellProps = {}) {
  const [amyOpen, setAmyOpen] = useState(false)
  const [amyPrompt, setAmyPrompt] = useState<string | null>(null)
  // Health/AI consent missing (next-start gate, or any 403 HEALTH_AI_CONSENT_REQUIRED): the consent screen
  // takes over the whole page — no rail, no bottom nav, no Amy — until the answer is recorded. The one
  // exception is Privacy & data (export / delete / withdraw): it renders in a stripped frame (no Amy, no
  // links into health surfaces) so a person who does not consent is never locked out of their data.
  const consentRequired = useHealthConsentRequired()
  const { t } = useI18n()
  const location = useLocation()
  // The route the CTA fires on, readable from the stable subscriber below.
  const currentPathRef = useRef(location.pathname + location.search)
  currentPathRef.current = location.pathname + location.search
  const { stale, deployedBuildId, checkNow } = useDeployFreshness(freshness)
  const executingBuildId = freshness?.currentBuildId ?? CLIENT_BUILD_ID
  // Dismissal is per deployed id: a NEWER deploy shows the banner again.
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null)
  const showDeployBanner = stale && deployedBuildId !== null && deployedBuildId !== dismissedBuildId

  const openDrawerWithPrompt = useCallback((prompt: string) => {
    setAmyPrompt(prompt)
    setAmyOpen(true)
  }, [])

  // Boot: finish a reload-before-prefill handoff — only when THIS build is
  // the one the reload aimed for and the route is where the CTA fired.
  // Any other stash is dropped here (one shot, no second reload).
  // Mount-only by design: the stash is consumed exactly once per boot.
  useEffect(() => {
    const outcome = takePendingPrefill(executingBuildId, currentPathRef.current)
    if (outcome.kind === "open") openDrawerWithPrompt(outcome.prompt)
  }, [executingBuildId, openDrawerWithPrompt])

  // ADR-027: the Insights page's "review my progress with Amy" CTA (and any
  // similar entry point — the recipe page's "Ask Amy to make room") opens
  // the drawer with a prefilled prompt. Acceptance 4: never on a superseded
  // bundle — a stale tab reloads first and the prompt survives the reload.
  useEffect(
    () =>
      onOpenAmyWithPrompt(prompt => {
        void checkNow().then(result => {
          if (result.stale && result.deployedBuildId) {
            stashPendingPrefill({ prompt, path: currentPathRef.current, buildId: result.deployedBuildId })
            stashPendingDraft(peekAmyDraft())
            reload()
            return
          }
          openDrawerWithPrompt(prompt)
        })
      }),
    [checkNow, reload, openDrawerWithPrompt]
  )

  if (consentRequired) {
    return isPrivacyDataPath(location.pathname) ? <ConsentExemptFrame /> : <HealthConsentGate />
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-cream text-ink md:flex-row">
      <div aria-hidden="true" className="app-atmosphere" />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-bb-md focus:bg-paper focus:px-4 focus:py-2 focus:text-ink focus:shadow-lg"
      >
        {t("nav.skipToContent")}
      </a>

      <LeftRail onOpenAmy={() => setAmyOpen(true)} />

      <div className="relative z-10 flex flex-1 flex-col">
        {showDeployBanner && (
          <div
            role="status"
            data-testid="deploy-freshness-banner"
            className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-2 rounded-bb-md border border-line bg-paper px-4 py-2 text-sm text-ink shadow-sm md:mx-8"
          >
            <span>{t("shell.deploy.newVersion")}</span>
            <span className="flex items-center gap-1">
              <button
                type="button"
                onClick={reload}
                className="inline-flex items-center gap-1 rounded-bb-full bg-green px-3 py-1 text-xs font-medium text-paper hover:opacity-90"
              >
                <RefreshCw className="size-3.5" aria-hidden="true" />
                {t("shell.deploy.reload")}
              </button>
              <button
                type="button"
                aria-label={t("shell.deploy.dismiss")}
                title={t("shell.deploy.dismiss")}
                onClick={() => setDismissedBuildId(deployedBuildId)}
                className="rounded-bb-sm p-1.5 text-ink-2 transition-colors hover:bg-cream hover:text-ink"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </span>
          </div>
        )}
        {/* `overflow-x-clip` (2026-09-10): the T21 ground washes bleed past the page
            padding by a fixed -1.5rem so they reach the screen edge, which at 390px
            pushed a decorative pseudo-element ~8px beyond the viewport and gave the
            whole app a horizontal scrollbar. `clip` — not `hidden` — kills the phantom
            scroll without creating a scroll container, so sticky positioning and the
            page's own vertical scrolling are untouched. */}
        <main id="main-content" className="flex-1 overflow-x-clip px-4 pt-4 pb-24 md:px-8 md:pb-8">
          <Outlet />
        </main>
      </div>

      <BottomNav onOpenAmy={() => setAmyOpen(true)} />
      <AmyDrawer
        open={amyOpen}
        onOpenChange={next => {
          setAmyOpen(next)
          if (!next) setAmyPrompt(null) // stale prompt never resurfaces on a later manual open
        }}
        initialPrompt={amyPrompt}
      />
    </div>
  )
}
